<script setup>
/* eslint semi: 1 */
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { FwbButton, FwbBadge } from 'flowbite-vue';
import EventBus from '@/components/mixins/event-bus-mixin';

const { t } = useI18n();
const $utils = inject('$utils');

const typeToTitleMap = {
  warning: 'label.warning',
  error: 'label.error',
};

const typeToAlertTypeMap = {
  warning: 'warning',
  error: 'danger',
};

const isVisible = ref(false);
const dialog = ref(null);
const callbacks = ref({});

const hasEventOKButtonClickCallback = computed(() => typeof callbacks.value.eventOKButtonClick === 'function');
const hasEventOnCloseCallback = computed(() => typeof callbacks.value.eventOnClose === 'function');
const hasCustomActions = computed(() => Array.isArray(dialog.value?.actions) && dialog.value.actions.length > 0);
const hasAlertMessageI18n = computed(() => !!dialog.value?.alertMessageI18n?.key);

const getPreviewImageSource = (item) => item?.preview ?? item?.origDetails?.preview;
const getPreviewType = (item) => getPreviewImageSource(item)?.type;
const getPreviewUrl = (item) => getPreviewImageSource(item)?.url;
const getPreviewFrameUrl = (item) => {
  const src = getPreviewUrl(item);
  if (!src) return '';

  return typeof $utils?.filterMediaPreviewUrl === 'function'
    ? $utils.filterMediaPreviewUrl(src)
    : src;
};
const getPreviewFrameDataSrc = (item) => {
  const src = getPreviewUrl(item);
  if (!src) return '';

  return typeof $utils?.trimDataSrc === 'function'
    ? $utils.trimDataSrc(src)
    : src;
};
const isI18nLinkParam = (param) => {
  return !!param && typeof param === 'object' && param.type === 'link' && !!param.href;
};
const isI18nStrongParam = (param) => {
  return !!param && typeof param === 'object' && param.type === 'strong';
};
const getI18nParamValue = (param) => {
  if (param && typeof param === 'object' && Object.prototype.hasOwnProperty.call(param, 'text')) {
    return param.text;
  }

  return param;
};

function resetState () {
  isVisible.value = false;
  callbacks.value = {};
}

function confirmAction () {
  if (hasEventOKButtonClickCallback.value) {
    callbacks.value.eventOKButtonClick();
  }

  resetState();
}

function closeNotification () {
  if (hasEventOnCloseCallback.value) {
    callbacks.value.eventOnClose();
  }

  resetState();
}

function actionButtonClick (action) {
  action?.onClick?.();

  if (!action?.keepOpen) {
    resetState();
  }
}

function onHideCustomNotification () {
  // Reset data on hide
  isVisible.value = false;
  dialog.value = null;
  callbacks.value = {};
}

function onShowCustomNotification (payload) {
  dialog.value = payload;

  // Apply common defaults for certain notification types
  const typeTitle = typeToTitleMap[dialog.value?.type];
  dialog.value.title = dialog.value?.title ?? (typeTitle ? t(typeTitle) : t('label.confirmation'));

  if (dialog.value?.alert) {
    const alertType = typeToAlertTypeMap[dialog.value?.type];
    if (alertType) {
      dialog.value.alert.type = alertType;
      dialog.value.alert.title = dialog.value.alert?.title ?? (typeTitle ? t(typeTitle) : undefined);
    }

    if (Array.isArray(dialog.value?.alert?.message)) {
      dialog.value.alert.message = dialog.value.alert.message.join('\n');
    }

    dialog.value.alert.icon = dialog.value.alert?.icon ?? 'flowbite:exclamation-circle-solid';
  }

  callbacks.value = payload.callbacks ?? {};
  isVisible.value = true;
}

onMounted(() => {
  EventBus.$on('SHOW_CUSTOM_NOTIFICATION', onShowCustomNotification);
  EventBus.$on('HIDE_CUSTOM_NOTIFICATION', onHideCustomNotification);
});

onBeforeUnmount(() => {
  EventBus.$off('SHOW_CUSTOM_NOTIFICATION', onShowCustomNotification);
  EventBus.$off('HIDE_CUSTOM_NOTIFICATION', onHideCustomNotification);
});
</script>
<template>
  <BaseCommonDialogModal
    id="customNotification"
    v-if="isVisible"
    container-class="arya-custom-notification z-[2000]"
    :class="{ 'is-visible': isVisible }"
    :title="dialog.title"
    size="lg"
    :alert="dialog?.alert"
    :persistent="dialog?.persistent ?? false"
    @close="closeNotification"
  >
    <template #default>
      <!-- Description -->
      <div class="text-sm text-gray-500 break-all text-left flex">
        <i18n-t
          v-if="dialog?.descriptionI18n"
          :keypath="dialog?.descriptionI18n?.key"
          tag="span"
        >
          <template
            v-for="(val, key) in dialog?.descriptionI18n?.params"
            #[key]
          >
            <strong>{{ val }}</strong>
          </template>
        </i18n-t>
        <template v-else>{{ dialog?.description }}</template>
      </div>

      <!-- Tags -->
      <div v-if="dialog?.tag" class="flex pt-2">
        <FwbBadge
          class="text-black!"
          :style="{
            backgroundColor: dialog.tag?.color ?? '#DCDFE5',
          }">
          {{ dialog.tag?.name || '' }}
        </FwbBadge>
      </div>

      <!-- Pop Id -->
      <div v-if="dialog?.popId" class="flex pt-2">
        <div class="popIdItem-slider-display">
          <div class="base">
            <div class="text">{{ dialog.popId?.name }}</div>
          </div>
          <div class="pop-icon"></div>
        </div>
      </div>

      <!-- Image Preview -->
      <div v-if="dialog?.preview">
        <p
          v-if="dialog?.preview?.title" class="text-left pl-3 pt-3 text-[#121212] text-xs"
        >
            {{ dialog.preview.title }}
        </p>
        <div
          :class="[
            'mt-3 flex flex-wrap gap-2 overflow-y-auto max-h-40',
            { 'justify-center': dialog.preview?.items.length < 4 },
          ]"
        >
          <div
            v-for="(item, index) in dialog.preview?.items"
            :key="index"
            class="flex flex-col items-center"
          >
            <div
              :class="[
                'w-30 shrink-0 overflow-hidden rounded bg-gray-100',
                dialog.preview?.mediaOrientation === 'vertical' ? 'aspect-[9/16]' : 'aspect-video',
              ]"
            >
              <img
                v-if="getPreviewType(item) === 'img'"
                :src="getPreviewImageSource(item).url"
                class="h-full w-full object-cover"
              />
              <iframe
                v-else-if="['svg', 'html'].includes(getPreviewType(item))"
                :src="getPreviewFrameUrl(item)"
                :data-src="getPreviewFrameDataSrc(item)"
                class="h-full w-full border-0"
              />
              <div
                v-else
                class="h-full w-full bg-gray-200"
              />
            </div>
            <span
              v-if="item?.name"
              class="text-xs text-[#9FA0AD] truncate w-30"
            >
              {{ item.name }}
            </span>
          </div>
        </div>
      </div>
    </template>
    <template #alertMessage>
      <i18n-t
        v-if="hasAlertMessageI18n"
        :keypath="dialog?.alertMessageI18n?.key"
        tag="span"
        class="whitespace-pre-line text-left"
      >
        <template
          v-for="(val, key) in dialog?.alertMessageI18n?.params"
          #[key]
        >
          <a
            v-if="isI18nLinkParam(val)"
            :href="val.href"
            :class="val.className ?? ''"
            :target="val.target ?? '_blank'"
            :rel="val.rel ?? 'noopener noreferrer'"
          >
            {{ getI18nParamValue(val) }}
          </a>
          <strong v-else-if="isI18nStrongParam(val)">
            {{ getI18nParamValue(val) }}
          </strong>
          <span v-else>
            {{ getI18nParamValue(val) }}
          </span>
        </template>
      </i18n-t>
      <span
        v-else
        class="whitespace-pre-line text-left"
      >
        {{ dialog?.alert?.message }}
      </span>
    </template>
    <template #actionButtons>
      <FwbButton
        v-if="!hasCustomActions && !dialog?.persistent"
        class="w-full"
        color="alternative"
        @click="closeNotification"
      >
        {{
          dialog?.closeButtonText ??
          (
            hasEventOKButtonClickCallback
              ? $t('buttons.cancel')
              : $t('buttons.close')
          )
        }}
      </FwbButton>
      <FwbButton
        v-for="(action, index) in dialog?.actions ?? []"
        :key="`custom-notification-action-${index}`"
        class="w-full justify-center"
        :color="action?.color ?? 'red'"
        @click="actionButtonClick(action)"
      >
        <template
          v-if="action?.icon"
          #prefix
        >
          <BaseIconSvg
            class="w-3! h-3!"
            :name="action.icon"
          />
        </template>
        {{ action?.text }}
      </FwbButton>
      <FwbButton
        v-if="!hasCustomActions && hasEventOKButtonClickCallback"
        class="w-full justify-center border-0"
        color="red"
        @click="confirmAction"
      >
        <template
          v-if="dialog?.confirmButtonIcon"
          #prefix
        >
          <BaseIconSvg
            class="w-3! h-3!"
            :name="dialog?.confirmButtonIcon"
          />
        </template>
        {{ dialog?.confirmButtonText ?? $t('label.confirm') }}
      </FwbButton>
    </template>
  </BaseCommonDialogModal>
</template>
<style scoped lang="scss">
@reference '#tailwind.scss';

.popIdItem-slider-display {
  @apply box-border relative w-[108px] h-[31px] break-normal;

  .pop-icon {
    @apply box-border absolute h-[31px] w-[31px] bg-[url('../../../static/icons/pop-displayed-diamond.svg')] bg-no-repeat bg-center;
  }

  .base {
    @apply box-border absolute w-[92.61px] h-[25px] bg-white m-0 p-0 rounded-tr-[3px] rounded-br-[3px] border-[1.1px] border-solid border-black left-[15px] top-[3px];
    -webkit-border-top-right-radius: 3px;
    -webkit-border-bottom-right-radius: 3px;
    -moz-border-radius-topright: 3px;
    -moz-border-radius-bottomright: 4px;

    .text {
      @apply absolute box-border w-14 h-[13px] text-center text-sm text-black font-semibold leading-[12.13px] break-normal w-[calc(100%-17px)] whitespace-nowrap overflow-hidden text-ellipsis m-0 p-0 left-6 left-[17px] top-[5.77px];
    }
  }
}
</style>
